import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useParams } from "react-router-dom";
import { useStore } from "../../app/stores/store";

import Header from "../common/header";
import Footer from "../common/footer";
import LoadingSpinner from "../common/LoadingSpinner";
import EmptyListComponent from "../common/EmptyListComponent";
import ServiceCard from "../services/ServiceCard";
import { VisaInclusions } from "../../components/visa-inclusions";
import { ApplicationSteps } from "../../components/application-steps";
import { WhyChooseUs } from "../../components/why-choose-us";
import { WhyChooseUsAwesome } from "../../components/why-choose-us-awesome";
import { DubaiVisaFaqs } from "../../components/dubai-visa-faqs";
import { RequiredDocuments } from "../../components/required-documents";
import { TermsAndConditions } from "../../components/terms-and-conditions";
import { ContactSection } from "../../components/contact-section";
import { Testimonials } from "../../components/testimonials";
import { Separator } from "../../components/ui/separator";
import { InquirySidebar } from "../../components/inquiry-sidebar";
import { KsaVisaPageAnimator } from "../../components/ksa-visa-page-animator";
import { UaeVisaSubNav } from "../../components/uae-visa-sub-nav";
import { TravelGuide } from "../../components/travel-guide";
import { DubaiFacts } from "../../components/dubai-facts";
import { OtherVisaPromotion } from "../../components/other-visa-promotion";
import { ServiceData } from "../../types/service";
import { PlaceHolderImages } from "../../lib/placeholder-images";
import { ResortDetailsCard } from "../../components/resort-details-card";
import LocationPanel from "../../components/location-section";
import { ResortInclusions } from "../../components/resort-inclusions";

// Reusable components from your new layout

const ServiceList = () => {
  const { id } = useParams();
  const {
    serviceStore: { services, loadServices },
    categoryStore: { currentCategory, getCategory },
    userStore,
  } = useStore();

  const [selectedVisa, setSelectedVisa] = useState<ServiceData>();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [highlightSidebar, setHighlightSidebar] = useState(false);
  const [isBookNowEnabled, setIsBookNowEnabled] = useState(false);

  useEffect(() => {
    if (id) {
      loadServices(id);
      getCategory(id);
    } else loadServices();
  }, [loadServices, userStore.token, id]);

  if (!services) return <LoadingSpinner />;

  // 📸 Placeholder images
  const hotelImage = PlaceHolderImages.find((p) => p.id === "hotel-exterior");
  const guestImage = PlaceHolderImages.find(
    (p) => p.id === "hotel-guest-photo"
  );
  const classicRoomImage1 = PlaceHolderImages.find(
    (p) => p.id === "classic-room-1"
  );
  const classicRoomImage2 = PlaceHolderImages.find(
    (p) => p.id === "classic-room-2"
  );
  const classicRoomImage3 = PlaceHolderImages.find(
    (p) => p.id === "classic-room-3"
  );
  const classicRoomImage4 = PlaceHolderImages.find(
    (p) => p.id === "classic-room-4"
  );
  const deluxeRoomImage1 = PlaceHolderImages.find(
    (p) => p.id === "deluxe-room-1"
  );
  const deluxeRoomImage2 = PlaceHolderImages.find(
    (p) => p.id === "deluxe-room-2"
  );
  const deluxeRoomImage3 = PlaceHolderImages.find(
    (p) => p.id === "deluxe-room-3"
  );
  const deluxeRoomImage4 = PlaceHolderImages.find(
    (p) => p.id === "deluxe-room-4"
  );

  // 🏨 Hotel mock data
  const hotelData = {
    name: "Majestic Premier Hotel Bur Dubai",
    rating: 4,
    images: {
      main: hotelImage,
      room: classicRoomImage1,
      guest: guestImage,
    },
    property: {
      description:
        "With close proximity to nearby city attractions, get a taste of luxury and comfort at Majestic Premier Hotel Bur Dubai while enjoying their in-house recreational activities.",
    },
    rooms: [
      {
        name: "Classic Room",
        size: "269 sq.ft (25 sq.mt)",
        view: "City View",
        beds: "2 Twin bed(s)",
        bathrooms: 1,
        features: ["Room with City View & Bathtub!", "Bathtub"],
        images: [
          classicRoomImage1,
          classicRoomImage2,
          classicRoomImage3,
          classicRoomImage4,
        ],
        bookingOptions: [
          {
            title: "Room Only",
            inclusions: [
              "No meals included",
              "15% off on food & beverage services",
              "Non-Refundable",
            ],
            oldPrice: "476",
            newPrice: "267",
            taxes: "83",
          },
          {
            title: "Room with Breakfast",
            inclusions: [
              "Early Check-In upto 4 hours (subject to availability)",
              "Non-Refundable",
            ],
            oldPrice: "374",
            newPrice: "318",
            taxes: "103",
          },
        ],
      },
      {
        name: "Deluxe Room",
        size: "269 sq.ft (25 sq.mt)",
        view: "City View",
        beds: "2 Twin bed(s)",
        bathrooms: 1,
        features: ["Room with City View & Bathtub!", "Bathtub"],
        images: [
          deluxeRoomImage1,
          deluxeRoomImage2,
          deluxeRoomImage3,
          deluxeRoomImage4,
        ],
        bookingOptions: [
          {
            title: "Room with Breakfast + Lunch/Dinner",
            inclusions: [
              "Early Check-In upto 4 hours (subject to availability)",
              "Non-Refundable",
            ],
            oldPrice: "526",
            newPrice: "469",
            taxes: "144",
          },
          {
            title: "Room With Breakfast + Lunch + Dinner",
            inclusions: [
              "Early Check-In upto 4 hours (subject to availability)",
              "Non-Refundable",
            ],
            oldPrice: "677",
            newPrice: "576",
            taxes: "186",
          },
        ],
      },
    ],
    review: { score: 4.3, count: 1404, label: "Excellent" },
    location: {
      name: "Bur Dubai",
      description:
        "4 minutes walk to Meena Bazaar I 6.8 km drive to Dubai Airport",
      rating: 4.5,
    },
    nearby: {
      attractions:
        "Have fun exploring Meena Bazaar (4 mins walk) & Bur Dubai Souk Market (1.6 km drive)",
      shopping:
        "Indulge in shopping at Day to Day Supermarket (6 mins walk) & BurJuman Mall (9 mins walk)",
      transport:
        "Property is near Dubai Airport (6.8 km drive) & Sharaf DG Metro Station (5 mins walk)",
    },
    amenities: ["Spa", "Swimming Pool", "Gym", "Jacuzzi", "Night Club"],
  };

  return (
    <div className="flex flex-col min-h-screen bg-white theme-ksa custom-container">
      <KsaVisaPageAnimator />
      <Header />

      <main className="flex-grow pt-24 md:pt-32">
        {/* 🏖️ HERO SECTION */}
        <section className="container mx-auto px-4 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            {currentCategory?.name === "Resorts" ? (
              <ResortDetailsCard
                hotel={hotelData}
                service={services[0]}
                services={services}
                selectedVisa={selectedVisa}
                highlightSidebar={highlightSidebar}
                setSelectedVisa={setSelectedVisa} // important to pass
              />
            ) : (
              <>
                <div className="space-y-4 text-start md:text-left">
                  <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-neutral-800 gsap-ksa-hero-title">
                    {currentCategory?.name}
                  </h1>
                  <div className="flex items-center justify-center md:justify-start gap-8 gsap-ksa-hero-subtitle">
                    <div>
                      <p className="text-muted-foreground">Up to 48 hours</p>
                      <p className="text-2xl font-bold text-primary">
                        Get Your Visa
                      </p>
                    </div>
                  </div>
                </div>

                <div className="w-full max-w-md gsap-ksa-hero-image">
                  <img
                    src="/global/uae_uber.webp"
                    alt="UAE cover image"
                    width={600}
                    height={400}
                    className="object-contain"
                    data-ai-hint="uae landmarks illustration"
                  />
                </div>
              </>
            )}
          </div>
        </section>

        {/* 🔹 Separator */}
        <section className="py-6">
          <div className="container mx-auto px-4 max-w-7xl">
            <Separator />
          </div>
        </section>

        {/* 🧩 SERVICES + SIDEBAR */}
        <section className="pb-16">
          <div className="container mx-auto max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Left Side: Service Grid */}

              <div className="lg:col-span-8">
                {currentCategory?.name === "Resorts" ? (
                  <LocationPanel />
                ) : (
                  <>
                    <UaeVisaSubNav />
                    <div
                      id="visas"
                      className="pt-2 grid grid-cols-1 md:grid-cols-2 gap-6"
                    >
                      {services.length > 0 ? (
                        services.map(
                          (service, index) =>
                            (service.categoryName === "Pick & Drop" ||
                              service.categoryName === "Visit Visa") && (
                              <div
                                key={index}
                                className="gsap-uae-visa-card w-full"
                              >
                                <ServiceCard
                                  key={service.id}
                                  service={service}
                                  setSelectedVisa={setSelectedVisa}
                                />
                              </div>
                            )
                        )
                      ) : (
                        <EmptyListComponent label="services" />
                      )}
                    </div>

                    {/* Other Static Sections */}
                    <div className="mt-12 space-y-12">
                      <div id="inclusions" className="gsap-uae-section pt-8">
                        <VisaInclusions />
                      </div>
                      <div id="steps" className="gsap-uae-section pt-8">
                        <ApplicationSteps />
                      </div>
                      <div id="why-us" className="gsap-uae-section pt-8">
                        <WhyChooseUs />
                      </div>
                      <div className="gsap-uae-section">
                        <WhyChooseUsAwesome />
                      </div>
                      <div id="faqs" className="gsap-uae-section pt-8">
                        <DubaiVisaFaqs />
                      </div>
                      <div id="travel-guide" className="gsap-uae-section pt-8">
                        <TravelGuide />
                      </div>
                      <div id="required-docs" className="gsap-uae-section pt-8">
                        <RequiredDocuments />
                      </div>
                      <div id="terms" className="gsap-uae-section pt-8">
                        <TermsAndConditions />
                      </div>
                      <div id="dubai-facts" className="gsap-uae-section pt-8">
                        <DubaiFacts />
                      </div>
                      <div id="contact" className="pt-8 gsap-uae-section">
                        <ContactSection />
                      </div>
                      <div className="pt-8 gsap-uae-section">
                        <OtherVisaPromotion />
                      </div>
                      <div className="pt-8 gsap-uae-section">
                        <Testimonials />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* 🧭 Right Side: Sidebar or ResortDetails */}
              <div id="sideForm" className="lg:col-span-4" ref={sidebarRef}>
                <div className="sticky top-28 gsap-ksa-sidebar">
                  {currentCategory?.name === "Resorts" ? (
                    <ResortInclusions />
                  ) : (
                    <InquirySidebar
                      visaOptions={services!}
                      selectedVisa={selectedVisa!}
                      highlight={highlightSidebar}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default observer(ServiceList);
